import React, { useState } from 'react'
import { GroupedAlerts, UngroupedAlert } from './Alerts'
import { Alert } from './types/Alert'
import { LoadingSpinnerRow } from './LoadingSpinnerRow'
import { GroupType } from './types/GroupType'
import { AlertType } from './types/AlertType'

const PARTIAL_LOAD_LENGTH = 100

interface AlertTableProps {
  alertType: AlertType
  onAlertClick?: (args: { fieldId: string; productId: string }) => void
}

export interface GroupedAlertTableProps extends AlertTableProps {
  groupType: GroupType.Product | GroupType.FieldAndMessage
  alerts: Alert[][]
}

export interface UngroupedAlertTableProps extends AlertTableProps {
  groupType: GroupType.None
  alerts: Alert[]
}

export const AlertTable = ({
  groupType,
  alertType,
  alerts,
  onAlertClick,
}: GroupedAlertTableProps | UngroupedAlertTableProps) => {
  const [shownLength, setShownLength] = useState(
    Math.min(PARTIAL_LOAD_LENGTH, alerts.length),
  )

  const extendShownAlerts = () =>
    setShownLength(Math.min(shownLength + PARTIAL_LOAD_LENGTH, alerts.length))

  const shownAlerts = alerts.slice(0, Math.min(shownLength, alerts.length))

  return (
    <table
      style={{
        borderCollapse: 'collapse',
        width: '100%',
      }}
    >
      <tbody>
        {groupType !== GroupType.None &&
          (shownAlerts as Alert[][]).map(groupedAlert => (
            <GroupedAlerts
              key={getGroupKey(groupType, groupedAlert[0])}
              groupType={groupType}
              alertType={alertType}
              alerts={groupedAlert}
              onAlertClick={onAlertClick}
            />
          ))}
        {groupType === GroupType.None && (
          <>
            {(shownAlerts as Alert[]).map(alert => {
              return (
                <UngroupedAlert
                  key={getGroupKey(GroupType.None, alert)}
                  alert={alert}
                  onClick={onAlertClick}
                />
              )
            })}
          </>
        )}
        {alerts.length > shownLength && (
          <LoadingSpinnerRow
            data-testid="extendShownLoader"
            key={shownLength}
            onVisible={extendShownAlerts}
          />
        )}
      </tbody>
    </table>
  )
}

const getGroupKey = (groupType: string, alert: Alert): string => {
  switch (groupType) {
    case GroupType.Product:
      return `productGroup-${alert.productId}`
    case GroupType.FieldAndMessage:
      return `fieldGroup-${alert.fieldId}-${alert.message}`
    case GroupType.None:
      return `ungrouped-${alert.fieldId}-${alert.message}-${alert.productId}`
    default:
      return `unknown-${alert.fieldId}-${alert.message}-${alert.productId}`
  }
}
